import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { deliveryRunAPI, orderAPI } from '../../services/api';
import { formatDateInput, dateKey, todayKey, addDays, weekdayOf } from '../../utils/date';
import { loadPreference, savePreference, clearPreference } from '../../utils/preferences';
import CreateRunModal from '../Planning/CreateRunModal';
import DiaryRunLoad from './DiaryRunLoad';
import '../Customers/Customers.css';
import './DeliveryDiary.css';

/**
 * The three time periods a run can start in. The API sends `period` and
 * `period_label` on every run payload, and that is the authoritative
 * answer - the grouping here must agree with the run code and the vehicle
 * count, which both use the same periods.
 */
const PERIODS = [
  { key: 'A', label: 'Morning' },
  { key: 'P', label: 'Afternoon' },
  { key: 'E', label: 'Evening' },
];

// formatDateInput() and dateKey() live in utils/date - every date in
// the app is built with them, so the diary and the run sheet cannot
// disagree about what a day is.

/** A bare date as "dd mm yyyy". */
const formatDateNumeric = (dateString) => {
  const [y, m, d] = dateKey(dateString).split('-');
  if (!y || !m || !d) return dateString;
  return `${d} ${m} ${y}`;
};

/**
 * The Monday-to-Sunday range the given day falls in.
 *
 * "This Week" is the calendar week, Monday to Sunday.
 */
const getWeekRangeFor = (dateString) => {
  const [y, m, d] = dateString.split('-').map(Number);
  const start = new Date(y, m - 1, d);
  start.setHours(0, 0, 0, 0);
  // getDay() is 0 for Sunday, so Monday is the day before that.
  const offset = start.getDay() === 0 ? 6 : start.getDay() - 1;
  start.setDate(start.getDate() - offset);

  const end = new Date(start);
  end.setDate(end.getDate() + 6);

  return { start: formatDateInput(start), end: formatDateInput(end) };
};

/** The week the diary shows by default - the one today falls in. */
const getWeekRange = () => getWeekRangeFor(formatDateInput(new Date()));

/**
 * Where the diary's date range is remembered between visits.
 *
 * A planner who spends the morning on one week does not want to set the
 * dates again tomorrow. The range is saved as the planner changes it and
 * restored when the diary is opened again.
 *
 * The DEFAULT week is deliberately not saved: "This Week" rolls, and a
 * frozen copy of it from last month would be worse than no memory at
 * all. Only a range the user actually chose is remembered.
 */
const DIARY_FILTERS_KEY = 'firewood.diary.filters';

/** The saved date range, or null if the planner has not chosen one. */
const loadDiaryFilters = () => {
  const saved = loadPreference(DIARY_FILTERS_KEY, null);
  if (!saved || typeof saved !== 'object') return null;
  return saved;
};

// Fallback for a payload that predates the period fields. Matches the API:
// before noon is Morning, until 3pm is Afternoon, 3pm onwards is Evening.
const periodKeyFor = (run) => {
  if (run.period) return run.period;

  const match = String(run.start_time || '').match(/(\d{2}):(\d{2})/);
  if (!match) return 'A';

  const hour = parseInt(match[1], 10);
  if (hour < 12) return 'A';
  return hour < 15 ? 'P' : 'E';
};

/**
 * How many cubes an order is worth.
 *
 * A cube is a loading position, so an order's cubes are the sum of
 * size x qty over its lines. This is the same arithmetic the Planning
 * page uses for its "N orders, N cubes total" hint, so the Diary and the
 * Planning page cannot give different answers for the same order.
 */
const cubesForOrder = (order) =>
  (order.order_lines || []).reduce((sum, line) => {
    return sum + (Number(line.size) || 0) * (Number(line.qty) || 0);
  }, 0);

const DeliveryRunsListPage = ({ user }) => {
  const navigate = useNavigate();

  const [deliveryRuns, setDeliveryRuns] = useState([]);
  // The orders in the range that are not on a run yet. The server decides
  // what "not on a run yet" means - the Diary never works it out for
  // itself.
  const [unallocatedOrders, setUnallocatedOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // This week is the default view, unless the planner has chosen a range
  // before - that one is remembered.
  const [startDate, setStartDate] = useState(
    () => loadDiaryFilters()?.startDate ?? getWeekRange().start
  );
  const [endDate, setEndDate] = useState(
    () => loadDiaryFilters()?.endDate ?? getWeekRange().end
  );
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedRun, setSelectedRun] = useState(null);
  const [cancelAction, setCancelAction] = useState('unallocate_only');
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    fetchDeliveryRuns();
  }, []);

  const fetchDeliveryRuns = async (overrideStartDate = null, overrideEndDate = null) => {
    try {
      setLoading(true);
      const params = {};
      
      const effectiveStartDate = overrideStartDate !== null ? overrideStartDate : startDate;
      const effectiveEndDate = overrideEndDate !== null ? overrideEndDate : endDate;

      if (effectiveStartDate) {
        params.start_date = effectiveStartDate;
      }
      if (effectiveEndDate) {
        params.end_date = effectiveEndDate;
      }

      const runs = await deliveryRunAPI.getAll(params);
      setDeliveryRuns(runs);
      setError(null);

      // The unallocated orders are fetched separately, and a failure
      // there must NOT take the diary down with it - the runs are the
      // main thing on the page.
      try {
        const unallocated = await orderAPI.getUnallocatedInRange(
          effectiveStartDate || null,
          effectiveEndDate || null
        );
        setUnallocatedOrders(unallocated || []);
      } catch (unallocatedErr) {
        console.error('Error fetching unallocated orders:', unallocatedErr);
        setUnallocatedOrders([]);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load delivery runs');
      console.error('Error fetching delivery runs:', err);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Set the date range and remember it.
   *
   * The range is saved as the planner changes it, so the diary opens on
   * the same dates next time. Saved in one place so the date inputs, the
   * preset buttons and the "range follows a newly created run" rule can
   * never drift apart.
   */
  const applyFilters = (nextStart, nextEnd) => {
    setStartDate(nextStart);
    setEndDate(nextEnd);
    savePreference(DIARY_FILTERS_KEY, { startDate: nextStart, endDate: nextEnd });
  };

  const handleFilter = (e) => {
    e.preventDefault();
    fetchDeliveryRuns();
  };

  const handleClearFilters = () => {
    // Clearing the filter also FORGETS it, so the diary goes back to the
    // rolling default week next time rather than to a permanently empty
    // date range.
    clearPreference(DIARY_FILTERS_KEY);
    setStartDate('');
    setEndDate('');
    fetchDeliveryRuns();
  };

  const handleViewDetails = (runId, runDate) => {
    // Navigate to the deliveries page (PlanningPage) with the selected date and run
    navigate(`/deliveries?date=${runDate}&run=${runId}`);
  };

  const handleCancelClick = (run) => {
    setSelectedRun(run);
    setCancelAction('unallocate_only');
    setRescheduleDate('');
    setShowCancelModal(true);
  };

  const handleConfirmCancel = async () => {
    if (!selectedRun) return;

    try {
      setCancelling(true);
      await deliveryRunAPI.cancel(
        selectedRun.id,
        cancelAction,
        cancelAction === 'reschedule' ? rescheduleDate : null
      );
      setShowCancelModal(false);
      setSelectedRun(null);
      fetchDeliveryRuns();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to cancel delivery run');
      console.error('Error cancelling delivery run:', err);
    } finally {
      setCancelling(false);
    }
  };

  const handleCloseCancelModal = () => {
    setShowCancelModal(false);
    setSelectedRun(null);
  };

  /**
   * A run created from the diary has to be visible in the diary.
   *
   * If it lands outside the range being shown - the planner is looking at
   * last week and creates a run for next month - the range moves to the
   * week the run belongs to. Otherwise the run is created and appears to
   * have vanished, which reads as the save having failed.
   */
  const handleRunCreated = async (run) => {
    const runDate = run ? dateKey(run.delivery_date) : '';

    const beforeStart = startDate && runDate && runDate < startDate;
    const afterEnd = endDate && runDate && runDate > endDate;

    if (beforeStart || afterEnd) {
      const range = getWeekRangeFor(runDate);
      applyFilters(range.start, range.end);
      await fetchDeliveryRuns(range.start, range.end);
      return;
    }

    await fetchDeliveryRuns();
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const [y, m, d] = dateKey(dateString).split('-').map(Number);
    if (!y || !m || !d) return dateString;
    return new Date(y, m - 1, d).toLocaleDateString('en-NZ', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 1:
        return 'Planning';
      case 2:
        return 'Confirmed';
      case 3:
        return 'Live';
      case 4:
        return 'Completed';
      default:
        return 'Unknown';
    }
  };

  // The diary styles its own status badges. `badge-info` and
  // `badge-warning` only exist in Admin.css, which is not loaded on this
  // page, so a Confirmed or Live run used to render with no colour at all.
  const getStatusClass = (status) => `diary-status diary-status-${status}`;

  const getDeliveryCount = (run) => {
    return run.deliveries?.length || 0;
  };

  /**
   * One card per date. Inside each card the runs are grouped by time
   * period, and a group with no runs in it is left out entirely.
   *
   * A date also gets a card when it has no runs at all but DOES have
   * unallocated orders - those orders need a run, and a date with
   * nothing but unallocated orders would otherwise be invisible. That is
   * the whole point of the planner looking at the diary.
   */
  const diary = useMemo(() => {
    const byDate = new Map();

    const cardFor = (date) => {
      if (!byDate.has(date)) byDate.set(date, { runs: [], unallocated: [] });
      return byDate.get(date);
    };

    deliveryRuns.forEach((run) => {
      cardFor(dateKey(run.delivery_date)).runs.push(run);
    });

    unallocatedOrders.forEach((order) => {
      cardFor(dateKey(order.schedule_date)).unallocated.push(order);
    });

    return Array.from(byDate.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, { runs, unallocated }]) => ({
        date,
        runs,
        unallocated,
        unallocatedCubes: unallocated.reduce(
          (sum, order) => sum + cubesForOrder(order),
          0
        ),
        groups: PERIODS
          .map((period) => ({
            ...period,
            runs: runs.filter((run) => periodKeyFor(run) === period.key),
          }))
          .filter((group) => group.runs.length > 0),
      }));
  }, [deliveryRuns, unallocatedOrders]);

  // Today and Tomorrow are flagged on their cards, so the planner does not
  // have to read the date to work out which one is which.
  const todayDateKey = todayKey();
  const tomorrowKey = addDays(todayDateKey, 1);

  const dateLabel = (dateString) => {
    const weekday = weekdayOf(dateString);
    if (dateString === todayDateKey) return `Today (${weekday} ${formatDateNumeric(dateString)})`;
    if (dateString === tomorrowKey) return `Tomorrow (${weekday} ${formatDateNumeric(dateString)})`;
    return `${weekday} ${formatDateNumeric(dateString)}`;
  };

  // Handle preset button click.
  //
  // "This Week" is the calendar week, Monday to Sunday. "Next 7 days" and
  // "Last 7 days" are rolling windows - the seven days from tomorrow, and
  // the seven days before today - so they do not snap to a week boundary
  // and leave the days in between uncovered.
  const handlePresetClick = (preset) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    let start;
    let end;

    switch (preset) {
      case 'lastWeek': {
        start = new Date(today);
        start.setDate(start.getDate() - 7);
        end = new Date(today);
        end.setDate(end.getDate() - 1);
        break;
      }
      case 'nextWeek': {
        start = new Date(today);
        start.setDate(start.getDate() + 1);
        end = new Date(today);
        end.setDate(end.getDate() + 7);
        break;
      }
      case 'twoWeeks':
      case 'threeWeeks': {
        const weeks = preset === 'twoWeeks' ? 2 : 3;
        const offset = today.getDay() === 0 ? 6 : today.getDay() - 1;
        start = new Date(today);
        start.setDate(start.getDate() - offset + weeks * 7);
        end = new Date(start);
        end.setDate(end.getDate() + 6);
        break;
      }
      default: {
        // This Week: Monday to Sunday.
        const offset = today.getDay() === 0 ? 6 : today.getDay() - 1;
        start = new Date(today);
        start.setDate(start.getDate() - offset);
        end = new Date(start);
        end.setDate(end.getDate() + 6);
      }
    }

    const effectiveStartDate = formatDateInput(start);
    const effectiveEndDate = formatDateInput(end);

    // Update state, and remember the range for next time.
    applyFilters(effectiveStartDate, effectiveEndDate);

    // Call fetch with the direct values (not using state which is stale on first render)
    fetchDeliveryRuns(effectiveStartDate, effectiveEndDate);
  };

  // Handle date range selection
  const handleStartDateChange = (e) => {
    applyFilters(e.target.value, endDate);
  };

  const handleEndDateChange = (e) => {
    applyFilters(startDate, e.target.value);
  };

  return (
    <div className="dashboard-container">
      <div className="dashboard-main-wrapper">
        <main className="dashboard-content">
          <div className="customer-list-container">
            <div className="customer-list-header">
              <h1>Delivery Diary</h1>
              <button
                type="button"
                className="btn btn-primary diary-new-run"
                onClick={() => setShowCreateModal(true)}
              >
                New Run
              </button>
            </div>

            <div className="customer-search diary-filters">
              <div className="filters-row">
                <div className="filter-group">
                  <label>Date From: </label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={handleStartDateChange}
                    className="filter-input"
                  />
                </div>
                <div className="filter-group">
                  <label>Date To: </label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={handleEndDateChange}
                    className="filter-input"
                  />
                </div>
                <button 
                  type="button" 
                  className="btn btn-primary"
                  onClick={() => fetchDeliveryRuns()}
                >
                  Filter
                </button>
                {(startDate || endDate) && (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleClearFilters}
                  >
                    Clear
                  </button>
                )}
              </div>
              <div className="filters-row presets-row">
                <button
                  type="button"
                  className="btn btn-sm btn-preset"
                  onClick={() => handlePresetClick('lastWeek')}
                  title="The seven days before today"
                >
                  Last 7 days
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-preset btn-preset--primary"
                  onClick={() => handlePresetClick('thisWeek')}
                  title="The default view - Monday to Sunday"
                >
                  This Week
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-preset btn-preset--secondary"
                  onClick={() => handlePresetClick('nextWeek')}
                  title="The seven days from tomorrow"
                >
                  Next 7 days
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-preset"
                  onClick={() => handlePresetClick('twoWeeks')}
                >
                  2 Weeks From Now
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-preset"
                  onClick={() => handlePresetClick('threeWeeks')}
                >
                  3 Weeks From Now
                </button>
              </div>
            </div>

            {error && <div className="error-message">{error}</div>}

            {loading ? (
              <div className="loading">Loading delivery runs...</div>
            ) : (
              <>
                {diary.length === 0 ? (
                  <div className="diary-empty">No delivery runs found</div>
                ) : (
                  <div className="diary-grid">
                    {diary.map(({ date, runs, groups, unallocated, unallocatedCubes }) => (
                      <section
                        className={`diary-date-card${date === todayDateKey ? ' diary-date-card--today' : ''}${
                          date === tomorrowKey ? ' diary-date-card--tomorrow' : ''
                        }`}
                        key={date}
                      >
                        <header className="diary-date-header">
                          <h2>{dateLabel(date)}</h2>
                          <span className="diary-date-summary">
                            {runs.length} {runs.length === 1 ? 'run' : 'runs'}
                            {unallocated.length > 0 && (
                              <span className="diary-date-unallocated-badge">
                                {unallocated.length} unallocated
                              </span>
                            )}
                          </span>
                        </header>

                        {groups.map((group) => (
                          <div className="diary-period" key={group.key}>
                            <h3 className="diary-period-heading">
                              {group.label}
                              <span className="diary-period-count">{group.runs.length}</span>
                            </h3>

                            <ul className="diary-run-list">
                              {group.runs.map((run) => (
                                <li
                                  className="diary-run"
                                  key={run.id}
                                  onClick={() => handleViewDetails(run.id, run.delivery_date)}
                                  title="Open this run"
                                >
                                  <div className="diary-run-head">
                                    <span className="diary-run-name">
                                      {run.name || run.code || '(No name or code)'}
                                    </span>
                                    <span className="diary-run-details">
                                      <span className="diary-run-count">
                                        {getDeliveryCount(run)}{' '}
                                        {getDeliveryCount(run) === 1 ? 'delivery' : 'deliveries'}
                                      </span>
                                      <span className={getStatusClass(run.status)}>
                                        {getStatusLabel(run.status)}
                                      </span>
                                      <button
                                        className="btn btn-sm btn-danger diary-run-cancel"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleCancelClick(run);
                                        }}
                                      >
                                        Cancel
                                      </button>
                                    </span>
                                  </div>

                                  {/* How full the vehicle is, and what is on it. */}
                                  <DiaryRunLoad run={run} />
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}

                        {/* Orders that are due on this date but are not
                            on a run yet. They need a run before the day
                            can be planned. */}
                        {unallocated.length > 0 && (
                          <div className="diary-unallocated">
                            <h3 className="diary-unallocated-heading">
                              Unallocated
                              <span className="diary-unallocated-count">
                                {unallocated.length}{' '}
                                {unallocated.length === 1 ? 'order' : 'orders'} ·{' '}
                                {unallocatedCubes}{' '}
                                {unallocatedCubes === 1 ? 'cube' : 'cubes'}
                              </span>
                            </h3>

                            <ul className="diary-unallocated-list">
                              {unallocated.map((order) => {
                                const cubes = cubesForOrder(order);
                                const reference =
                                  order.order_external_reference || `Order #${order.id}`;

                                return (
                                  <li className="diary-unallocated-order" key={order.id}>
                                    <span className="diary-unallocated-ref">{reference}</span>
                                    <span className="diary-unallocated-cubes">
                                      {cubes} {cubes === 1 ? 'cube' : 'cubes'}
                                    </span>
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        )}
                      </section>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </main>
      </div>

      {/* Create Run Modal - the same dialog the Planning page uses */}
      <CreateRunModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreated={handleRunCreated}
        defaultDate={tomorrowKey}
      />

      {/* Cancel Confirmation Modal */}
      {showCancelModal && selectedRun && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Cancel Delivery Run</h3>
            <p>
              Are you sure you want to cancel the delivery run scheduled for{' '}
              <strong>{formatDate(selectedRun.delivery_date)}</strong>?
            </p>
            
            {getDeliveryCount(selectedRun) > 0 && (
              <>
                <p className="warning-text">
                  This run has <strong>{getDeliveryCount(selectedRun)}</strong> delivery/deliveries assigned.
                </p>
                
                <div className="cancel-options">
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="cancelAction"
                      value="unallocate_only"
                      checked={cancelAction === 'unallocate_only'}
                      onChange={(e) => setCancelAction(e.target.value)}
                    />
                    <span><strong>Unallocate deliveries only</strong><br/>
                    <small>Keep customer orders at their current dates (they will need to be manually rescheduled later)</small></span>
                  </label>
                  
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="cancelAction"
                      value="reschedule"
                      checked={cancelAction === 'reschedule'}
                      onChange={(e) => setCancelAction(e.target.value)}
                    />
                    <span><strong>Reschedule orders to a new date</strong><br/>
                    <small>Move all customer orders to a new date and remove them from this run</small></span>
                  </label>
                  
                  {cancelAction === 'reschedule' && (
                    <div className="reschedule-date-input">
                      <label htmlFor="rescheduleDate">New schedule date:</label>
                      <input
                        type="date"
                        id="rescheduleDate"
                        value={rescheduleDate}
                        onChange={(e) => setRescheduleDate(e.target.value)}
                        className="filter-input"
                      />
                    </div>
                  )}
                </div>
              </>
            )}

            {getDeliveryCount(selectedRun) === 0 && (
              <p className="info-text">
                This run has no deliveries assigned. It will be deleted directly.
              </p>
            )}

            <div className="modal-actions">
              <button
                className="btn btn-secondary"
                onClick={handleCloseCancelModal}
                disabled={cancelling}
              >
                Keep Run
              </button>
              <button
                className="btn btn-danger"
                onClick={handleConfirmCancel}
                disabled={cancelling || (cancelAction === 'reschedule' && !rescheduleDate)}
              >
                {cancelling ? 'Cancelling...' : 'Confirm Cancellation'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeliveryRunsListPage;
